import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Empty } from 'antd';
import dayjs from 'dayjs';
import { AlarmClockOff, AlertTriangle, CalendarCheck2, CalendarOff, ClipboardList, Clock, LogIn, Lock } from 'lucide-react';
import Layout from '../../Layout';
import { useHr } from './hrStore';
import { tpApi, useTp } from './tpStore';
import { LEAVE_KIND, branchById, fmtD, isWorking, positionById, toDay } from './hrLogic';
import {
  WD, coverage, evalAssign, fmtMonth, leaveOn, monthOf, reqSummary, shiftAt, shiftById, shiftLabel, weekDays,
} from './tpLogic';
import { Avatar, Badge, Kpi, LevelDot, PageHeader, Panel } from './hrUi';
import { BranchSelect, ReqKindTag, ShiftChip, done, useTpPerms } from './tpUi';

/** Tổng quan chấm công hôm nay: ai đang trong ca, chưa chấm vào, đi muộn, nghỉ; ca thiếu người 7 ngày tới; đơn chờ duyệt; tình trạng chốt công */
export default function TimeDashboard() {
  const s = useHr();
  const t = useTp();
  const navigate = useNavigate();
  const { canApprove, me } = useTpPerms();
  const [params, setParams] = useSearchParams();
  const branchId = params.get('branch') || '';
  const today = toDay();
  const now = Date.now();
  const inBranch = (bid: string) => !branchId || bid === branchId;

  const todays = useMemo(() => t.assigns.filter(a => a.date === today && inBranch(a.branchId)).map(a => {
    const sh = shiftById(t, a.shiftId)!;
    const e = s.employees.find(x => x.id === a.empId)!;
    return { a, sh, e, ev: evalAssign(a, sh, { now, grace: t.time.graceMin, leave: (e && leaveOn(e, today)?.part) === 1 }) };
  }).filter(x => x.e), [t, s, today, branchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const started = todays.filter(x => shiftAt(x.a.date, x.sh).startAt + t.time.graceMin * 60000 < now && x.ev.state !== 'leave');
  const missing = started.filter(x => !x.a.inAt);
  const inShift = todays.filter(x => x.a.inAt && !x.a.outAt);
  const late = todays.filter(x => x.a.inAt && x.a.inAt > shiftAt(x.a.date, x.sh).startAt + t.time.graceMin * 60000);
  const onLeave = s.employees.filter(e => inBranch(e.branchId) && isWorking(e, today) && leaveOn(e, today));
  const pending = t.requests.filter(r => r.status === 'pending' && inBranch(s.employees.find(e => e.id === r.empId)?.branchId || ''))
    .sort((a, b) => a.at - b.at);
  const next7 = weekDays(today);
  const gaps = s.branches.filter(b => inBranch(b.id)).flatMap(b => coverage(s, t, b.id, next7));
  const prev = dayjs().subtract(1, 'month').format('YYYY-MM');
  const branchesWithStaff = s.branches.filter(b => inBranch(b.id) && s.employees.some(e => e.branchId === b.id && isWorking(e, `${prev}-28`)));
  const period = t.periods.find(p => p.month === prev);

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Tổng quan chấm công" desc={`${WD[dayjs().day()]} ${fmtD(today)} · cập nhật theo giờ chấm và lịch ca`}
          extra={<BranchSelect value={branchId} onChange={v => setParams(v ? { branch: v } : {}, { replace: true })} />} />

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Kpi icon={<CalendarCheck2 size={18} />} tone="bg-sky-50 text-sky-600" label="Ca hôm nay" value={todays.length} hint={`${new Set(todays.map(x => x.e.id)).size} người`} onClick={() => navigate('/task-assignment')} />
          <Kpi icon={<LogIn size={18} />} tone="bg-emerald-50 text-emerald-600" label="Đang trong ca" value={inShift.length} />
          <Kpi icon={<AlarmClockOff size={18} />} tone={missing.length ? 'bg-rose-50 text-rose-600' : 'bg-gray-50 text-gray-500'} label="Chưa chấm vào ca" value={missing.length} hint={missing.some(x => shiftAt(x.a.date, x.sh).endAt < now) ? `${missing.filter(x => shiftAt(x.a.date, x.sh).endAt < now).length} người đã hết ca` : undefined} />
          <Kpi icon={<Clock size={18} />} tone={late.length ? 'bg-amber-50 text-amber-600' : 'bg-gray-50 text-gray-500'} label="Đi muộn hôm nay" value={late.length} />
          <Kpi icon={<ClipboardList size={18} />} tone={pending.length ? 'bg-violet-50 text-violet-600' : 'bg-gray-50 text-gray-500'} label="Đơn chờ duyệt" value={pending.length} onClick={() => navigate('/time-requests')} />
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <Panel title={`Chưa chấm vào ca (${missing.length})`} icon={<AlarmClockOff size={15} className="text-rose-500" />}>
            {missing.length === 0 ? <Empty className="py-6" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Mọi người đã vào ca" /> : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800 max-h-[320px] overflow-auto">
                {missing.map(({ a, sh, e }) => (
                  <div key={a.id} className="flex items-center gap-3 px-4 py-2 text-sm">
                    <Avatar name={e.name} />
                    <div className="min-w-0 flex-1"><div className="font-medium truncate">{e.name}</div><div className="text-[11px] text-gray-400 truncate">{positionById(s, e.positionId)?.name} · {branchById(s, a.branchId)?.short}</div></div>
                    <ShiftChip sh={sh} extra={<span className="font-normal">{shiftLabel(sh)}</span>} />
                    <span className="text-xs text-rose-600 w-[90px] text-right">{shiftAt(a.date, sh).endAt < now ? 'Hết ca, không chấm' : `trễ ${Math.round((now - shiftAt(a.date, sh).startAt) / 60000)}′`}</span>
                    <a href={`tel:${e.phone}`} className="text-xs text-[#3e91ab] hover:underline">Gọi</a>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title={`Đơn chờ duyệt (${pending.length})`} icon={<ClipboardList size={15} className="text-violet-500" />}
            extra={<button type="button" className="text-xs text-[#3e91ab] hover:underline" onClick={() => navigate('/time-requests')}>Xem tất cả ›</button>}>
            {pending.length === 0 ? <Empty className="py-6" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có đơn chờ duyệt" /> : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800 max-h-[320px] overflow-auto">
                {pending.slice(0, 8).map(r => {
                  const e = s.employees.find(x => x.id === r.empId);
                  const a = t.assigns.find(x => x.id === r.assignId);
                  return (
                    <div key={r.id} className="flex items-center gap-3 px-4 py-2 text-sm">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2"><span className="font-medium truncate">{e?.name}</span><ReqKindTag kind={r.kind} /></div>
                        <div className="text-[11px] text-gray-500 truncate">{reqSummary(r, a ? shiftById(t, a.shiftId)?.name : undefined)} · {r.reason}</div>
                      </div>
                      {canApprove && <Button size="small" type="primary" onClick={() => done(tpApi.decide(r.id, true, me), `Đã duyệt ${r.code}`)}>Duyệt</Button>}
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>

          <Panel title={`Ca thiếu người / thiếu bác sĩ 7 ngày tới (${gaps.length})`} icon={<AlertTriangle size={15} className="text-amber-500" />}
            extra={<button type="button" className="text-xs text-[#3e91ab] hover:underline" onClick={() => navigate('/task-assignment')}>Mở phân ca ›</button>}>
            {gaps.length === 0 ? <Empty className="py-6" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Các ca đủ định mức" /> : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800 max-h-[320px] overflow-auto">
                {gaps.map(g => (
                  <button key={`${g.date}${g.branchId}${g.text}`} type="button" onClick={() => navigate(`/task-assignment?branch=${g.branchId}&w=${g.date}`)}
                    className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/40">
                    <LevelDot level={g.level} />
                    <span className="w-[90px] text-xs text-gray-500">{WD[dayjs(g.date).day()]} {dayjs(g.date).format('DD/MM')}</span>
                    <span className="w-[80px] text-xs">{branchById(s, g.branchId)?.short}</span>
                    <span className="flex-1">{g.text}</span>
                  </button>
                ))}
              </div>
            )}
          </Panel>

          <div className="flex flex-col gap-4">
            <Panel title={`Chốt công ${fmtMonth(prev)}`} icon={<Lock size={15} className="text-emerald-600" />}
              extra={<button type="button" className="text-xs text-[#3e91ab] hover:underline" onClick={() => navigate(`/timesheet?m=${prev}`)}>Bảng công ›</button>}>
              <div className="p-4 flex flex-wrap gap-2 text-sm">
                {branchesWithStaff.map(b => {
                  const lock = t.locks.find(l => l.month === prev && l.branchId === b.id);
                  return <Badge key={b.id} className={lock ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>{b.short}: {lock ? `đã chốt ${dayjs(lock.at).format('DD/MM')}` : 'chưa chốt'}</Badge>;
                })}
                {period && <button type="button" onClick={() => navigate(`/salary?id=${period.id}`)} className="text-xs text-[#3e91ab] hover:underline w-full text-left mt-1">Bảng lương {fmtMonth(prev)}: {({ draft: 'nháp', pending: 'chờ duyệt', approved: 'đã duyệt', paid: 'đã chi' })[period.status]} ›</button>}
              </div>
            </Panel>
            <Panel title={`Nghỉ hôm nay (${onLeave.length})`} icon={<CalendarOff size={15} className="text-violet-500" />}>
              {onLeave.length === 0 ? <div className="px-4 py-3 text-xs text-gray-400">Không ai nghỉ</div> : (
                <div className="px-4 py-3 flex flex-wrap gap-1.5">
                  {onLeave.map(e => <Badge key={e.id} className={LEAVE_KIND[leaveOn(e, today)!.r.kind].badge}>{e.name} · {LEAVE_KIND[leaveOn(e, today)!.r.kind].label.split(' (')[0]}</Badge>)}
                </div>
              )}
            </Panel>
          </div>
        </div>
        <div className="text-[11px] text-gray-400">Tháng {fmtMonth(monthOf(today))}: chấm công đang mở. Chốt công từng chi nhánh sau khi hết tháng và duyệt hết đơn.</div>
      </div>
    </Layout>
  );
}
